import fuzzy from 'fuzzy';
import memoize from 'lru-memoize';
import React, {FC, ReactNode, useMemo, useState} from 'react';

import {useLocalize} from '../../../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {Localizer} from '../../../../../../../libs/shared/core/src/helpers/internationalization/localizeTypes';
import {
  defineMessage,
  Trans,
} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ChannelTypesEnum} from '../../../../../../../libs/shared/core/src/models/api/channelCommonTypes';
import {MessageTypesEnum} from '../../../../../../../libs/shared/core/src/models/api/commonMessageModel';
import {Button} from '../../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {DropdownItem} from '../../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemCheckbox} from '../../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemCheckbox';
import {DropdownItemContent} from '../../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {DropdownItemIcon} from '../../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemIcon';
import {SearchDropdown} from '../../../../../../../libs/shared/ui/src/components/dropdowns/search/searchDropdown';
import {
  ChannelIcon,
  ChannelIconSizesEnum,
} from '../../../../../../../libs/shared/ui/src/components/icon/channelIcon';
import {Icon, IconName} from '../../../../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../../../libs/shared/ui/src/styles/commonStyles';

/*
 * Constants.
 */

const dropdownWidth = 250; // px

export type SearchableMessageTypesEnum =
  | Exclude<MessageTypesEnum, MessageTypesEnum.LAYER | MessageTypesEnum.INTERNAL | MessageTypesEnum.CALL>
  | 'discussion'
  | 'phone-call';

const searchableMessageTypes: ReadonlyArray<SearchableMessageTypesEnum> = [
  MessageTypesEnum.EMAIL,
  'discussion',
  MessageTypesEnum.FRONT_CHAT,
  MessageTypesEnum.SMS,
  MessageTypesEnum.WHATSAPP,
  MessageTypesEnum.YALO_WHATSAPP,
  MessageTypesEnum.CUSTOM,
  MessageTypesEnum.FACEBOOK,
  MessageTypesEnum.GOOGLE_PLAY,
  MessageTypesEnum.INTERCOM,
  MessageTypesEnum.SMOOCH,
  'phone-call',
  MessageTypesEnum.TWEET,
  MessageTypesEnum.TWEET_DM,
] as const;

/*
 * Props.
 */

interface MultiSelectMessageTypesDropdownProps {
  popoverProps: PopoverTransferProps;
  initialSelection: ReadonlySet<SearchableMessageTypesEnum>;
  onSelectionChange: (arg: ReadonlySet<SearchableMessageTypesEnum>) => void;
}

/*
 * Component.
 */

export const MultiSelectMessageTypesDropdown: FC<MultiSelectMessageTypesDropdownProps> = (props) => {
  const {popoverProps, initialSelection, onSelectionChange} = props;

  const localize = useLocalize();
  const [selection, setSelection] = useState(new Set(initialSelection));

  const [query, setQuery] = useState('');
  const filteredData = useMemo(() => filterRows(searchableMessageTypes, query, localize), [query, localize]);

  const reportChangeAndClose = () => {
    onSelectionChange(selection);
    popoverProps.onRequestClose?.();
  };

  return (
    <SearchDropdown
      {...popoverProps}
      onRequestClose={reportChangeAndClose}
      width={dropdownWidth}
      data={filteredData}
      headerTitle={<Trans id="customerview-search-channeltype-title">Channel type</Trans>}
      headerRightIcon={
        selection.size > 0 ? (
          <Button
            variant={ButtonVariantsEnum.LINK}
            size={VisualSizesEnum.SMALL}
            onClick={() => {
              selection.clear();
              setSelection(selection);
              reportChangeAndClose();
            }}
          >
            <Trans id="clear-refinement-button-link">Clear</Trans>
          </Button>
        ) : null
      }
      renderRow={(item) => {
        const channelName = renderChannelTypeName(item, localize);

        return (
          <DropdownItem
            onSelect={(event) => {
              // Prevent the dropdown from closing.
              event.preventDefault();
              const newSelection = new Set(selection);

              if (newSelection.has(item)) {
                newSelection.delete(item);
              } else {
                newSelection.add(item);
              }

              setSelection(newSelection);
            }}
            renderRightContent={() => <DropdownItemCheckbox isChecked={selection.has(item)} />}
          >
            <DropdownItemIcon label={channelName}>{renderMessageTypeIcon(item)}</DropdownItemIcon>
            <DropdownItemContent>{channelName}</DropdownItemContent>
          </DropdownItem>
        );
      }}
      query={query}
      onQueryChange={setQuery}
    />
  );
};

/*
 * Helpers.
 */

const filterRows = memoize(20)(
  (rows: ReadonlyArray<SearchableMessageTypesEnum>, query: string, localize: Localizer) => {
    if (!query) {
      return rows;
    }

    return rows.filter((t) =>
      fuzzy.match(query, `${t} ${renderChannelTypeName(t, localize)}`, {caseSensitive: false}),
    );
  },
);

function renderChannelTypeName(channelType: SearchableMessageTypesEnum, localize: Localizer) {
  switch (channelType) {
    case 'phone-call':
      return localize(defineMessage({id: 'channel-type-dropdown-item-call', message: 'Phone call'}));
    case 'discussion':
      return localize(defineMessage({id: 'channel-type-dropdown-item-discussion', message: 'Discussion'}));
    case MessageTypesEnum.CUSTOM:
      return localize(defineMessage({id: 'channel-type-dropdown-item-custom', message: 'Custom channel'}));
    case MessageTypesEnum.EMAIL:
      return localize(defineMessage({id: 'channel-type-dropdown-item-email', message: 'Email'}));
    case MessageTypesEnum.GOOGLE_PLAY:
      return localize(defineMessage({id: 'channel-type-dropdown-item-google-play', message: 'Google Play'}));
    case MessageTypesEnum.FRONT_CHAT:
      return localize(defineMessage({id: 'channel-type-dropdown-item-front-chat', message: 'Front chat'}));
    case MessageTypesEnum.SMS:
      return localize(defineMessage({id: 'channel-type-dropdown-item-sms', message: 'SMS'}));
    case MessageTypesEnum.WHATSAPP:
      return localize(
        defineMessage({id: 'channel-type-dropdown-item-twilio-whatsapp', message: 'Twilio / Whatsapp'}),
      );
    case MessageTypesEnum.YALO_WHATSAPP:
      return localize(
        defineMessage({id: 'channel-type-dropdown-item-yalo-whatsapp', message: 'Yalo / Whatsapp'}),
      );
    case MessageTypesEnum.INTERCOM:
      return localize(defineMessage({id: 'channel-type-dropdown-item-intercom', message: 'Intercom'}));
    case MessageTypesEnum.SMOOCH:
      return localize(
        defineMessage({id: 'channel-type-dropdown-item-smooch', message: 'Sunshine Conversations'}),
      );
    case MessageTypesEnum.TWEET:
      return localize(defineMessage({id: 'channel-type-dropdown-item-twitter', message: 'Twitter'}));
    case MessageTypesEnum.TWEET_DM:
      return localize(defineMessage({id: 'channel-type-dropdown-item-twitter-dm', message: 'Twitter DM'}));
    case MessageTypesEnum.FACEBOOK:
      return localize(defineMessage({id: 'channel-type-dropdown-item-facebook', message: 'Messenger'}));
    default:
      channelType satisfies never;
      return null;
  }
}

function messageTypeToChannelTypeForIconPurposes(
  messageType: SearchableMessageTypesEnum,
): ChannelTypesEnum | undefined {
  switch (messageType) {
    case MessageTypesEnum.CUSTOM:
      return ChannelTypesEnum.CUSTOM;
    case MessageTypesEnum.EMAIL:
      return ChannelTypesEnum.IMAP;
    case MessageTypesEnum.GOOGLE_PLAY:
      return ChannelTypesEnum.GOOGLE_PLAY;
    case MessageTypesEnum.FRONT_CHAT:
      return ChannelTypesEnum.FRONT_CHAT;
    case MessageTypesEnum.WHATSAPP:
      return ChannelTypesEnum.TWILIO_WHATSAPP;
    case MessageTypesEnum.YALO_WHATSAPP:
      return ChannelTypesEnum.YALO_WHATSAPP;
    case MessageTypesEnum.INTERCOM:
      return ChannelTypesEnum.INTERCOM;
    case MessageTypesEnum.SMOOCH:
      return ChannelTypesEnum.SMOOCH;
    case MessageTypesEnum.TWEET:
      return ChannelTypesEnum.TWITTER;
    case MessageTypesEnum.TWEET_DM:
      return ChannelTypesEnum.TWITTER_DM;
    case MessageTypesEnum.FACEBOOK:
      return ChannelTypesEnum.FACEBOOK;
    case MessageTypesEnum.SMS:
      return ChannelTypesEnum.TWILIO;
    default:
      return undefined;
  }
}

function messageTypeToIconNamePartial(messageType: string): IconName | undefined {
  switch (messageType) {
    case 'discussion':
      return 'discussionSmall';
    case 'phone-call':
      return 'phoneSmall';
    default:
      return undefined;
  }
}

export function renderMessageTypePillLabel(
  selectedValues: readonly SearchableMessageTypesEnum[],
  placeholder: ReactNode,
  localize: Localizer,
) {
  if (selectedValues.length === 0) {
    return placeholder;
  }

  const firstSelectedValue = selectedValues[0];
  const firstSelectValueLabel = renderChannelTypeName(firstSelectedValue, localize);

  return (
    <span>
      {firstSelectValueLabel}
      {selectedValues.length > 1 ? `, +${selectedValues.length - 1}` : null}
    </span>
  );
}

export function renderMessageTypeIcon(
  value: SearchableMessageTypesEnum,
  channelIconSize = ChannelIconSizesEnum.REGULAR,
  regularIconSize = 20,
): ReactNode {
  const channelType = messageTypeToChannelTypeForIconPurposes(value);

  if (channelType) {
    return <ChannelIcon type={channelType} size={channelIconSize} />;
  }

  const iconName = messageTypeToIconNamePartial(value) ?? 'gearSmall';
  return <Icon name={iconName} size={regularIconSize} />;
}
